/*
 * Copyright (c) 2021. Datafood srl
 * Developed by Revo Digital - revodigital.it
 * ---
 * Author: leonardoviada
 * File: auth-actions.ts
 * Project: mle-client
 * Committed last: 2021/10/27 @ 1149
 * ---
 */

import {createAsyncThunk} from '@reduxjs/toolkit';
import {mleAuthProviderRestClient} from '../../api/ap';
import {MleAuthProviderEndpoints} from '../../api/ap/endpoints';
import {TokenService} from '../../api/ap/token.service';
import {idleAll, idleOne, runOne, runRoot} from '../slices/loading-slice';
import {redirect} from '../../common/utils';
import {LoadingElementsIds} from '../../common/loading-elements-ids';
import {AuthPaths} from '../../paths/auth.paths';
import {MleToaster, Toasts} from '../../common/toast';
import {RootPaths} from '../../paths/root.paths';
import {mleDomainGqlClient} from '../../api/domain';
import {CREATE_COMPANY, CREATE_USER} from '../../api/domain/mutations.gql';
import {RegisterFormValues} from '../../components/forms/RegisterForm';
import {GET_USER_BY_ID} from '../../api/domain/queries.gql';
import {MyLabelAuthFeature} from "../features/mylabel/auth/MyLabelAuthFeature";

export interface LocalLoginCredentials {
    email: string;
    password: string;
}

export const localLogin = createAsyncThunk(
    'auth/login',
    async (credentials: LocalLoginCredentials, thunkAPI) => {
        thunkAPI.dispatch(runOne(LoadingElementsIds.LOGIN));

        try {
            const {data} = await mleAuthProviderRestClient.post(
                MleAuthProviderEndpoints.LOCAL_LOGIN,
                credentials
            );
            TokenService.getInstance().setTokens(data.tokens);

            const userRes = await mleDomainGqlClient.query({
                query: GET_USER_BY_ID,
                variables: {
                    id: parseInt(data.authEntity.instanceId)
                }
            });

            thunkAPI.dispatch(idleOne(LoadingElementsIds.LOGIN));

            if (!data.authEntity.instanceId) {
                redirect(AuthPaths.LOGIN);
                MleToaster.getInstance().show(Toasts.AE_NOT_LINKED);
            } else {
                redirect(RootPaths.DASHBOARD);
                MleToaster.getInstance().show(Toasts.LOGIN_SUCCESSFUL);
            }

            return {...data.authEntity, user: userRes.data.user};
        } catch (e) {
            return undefined;
        }
    }
);

export const registerAndBoard = createAsyncThunk(
    'auth/registerAndBoard',
    async (values: RegisterFormValues, thunkAPI) => {
        thunkAPI.dispatch(runRoot());
        const registerRes = await mleAuthProviderRestClient.post(
            MleAuthProviderEndpoints.LOCAL_REGISTER,
            {
                email: values.email,
                strategySpecificData: {
                    password: values.password
                }
            }
        );
        TokenService.getInstance().setTokens(registerRes.data.tokens);
        const companyRes = await mleDomainGqlClient.mutate({
            mutation: CREATE_COMPANY,
            variables: {
                input: {
                    name: values.companyName,
                    taxCode: '',
                    fiscalCode: '',
                    reaCode: '',
                    pec: '',
                    email: '',
                    address: '',
                }
            }
        });
        const userRes = await mleDomainGqlClient.mutate({
            mutation: CREATE_USER,
            variables: {
                input: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    phone: '',
                    companyId: companyRes.data.addCompany.id
                }
            }
        });
        await mleAuthProviderRestClient.post(
            MleAuthProviderEndpoints.LINK_INSTANCE, {
                instanceId: userRes.data.addUser.id + ''
            });


        thunkAPI.dispatch(idleAll());
        MleToaster.getInstance().show(Toasts.REGISTRATION_SUCCESSFUL);
        redirect(RootPaths.DASHBOARD);

        return {
            ...registerRes.data.authEntity,
            user: userRes.data.addUser
        };
    }
);


export const me = createAsyncThunk(
    'auth/me',
    async (o: undefined, thunkAPI) => {
        thunkAPI.dispatch(runRoot());
        const {data} = await mleAuthProviderRestClient.get(
            MleAuthProviderEndpoints.ME);

        const userRes = await mleDomainGqlClient.query({
            query: GET_USER_BY_ID,
            variables: {
                id: parseInt(data.instanceId)
            }
        });

        thunkAPI.dispatch(idleAll());

        return {...data, user: userRes.data.user};
    }
);

export const logout = createAsyncThunk(
    'auth/logout',
    async (o: undefined, thunkAPI) => {
        thunkAPI.dispatch(runRoot());
        const {data} = await mleAuthProviderRestClient.post(
            MleAuthProviderEndpoints.LOGOUT);

        TokenService.getInstance().clearTokens();
        // Rimuove le credenziali salvate
        MyLabelAuthFeature.removeConfiguration();

        redirect(AuthPaths.LOGIN);
        thunkAPI.dispatch(idleAll());
        MleToaster.getInstance().show(Toasts.LOGOUT_SUCCESSFUL);

        return data;
    }
);

export const getNewAccessToken = createAsyncThunk(
    'auth/refresh',
    async (o: undefined, thunkAPI) => {
        thunkAPI.dispatch(runRoot());
        const {data} = await mleAuthProviderRestClient.post(
            MleAuthProviderEndpoints.REFRESH, {
                refreshToken: TokenService.getInstance().getRefreshToken()
            });
        TokenService.getInstance().setAccessToken(data.accessToken);
        thunkAPI.dispatch(idleAll());

        return data;
    }
);

export const linkInstance = createAsyncThunk(
    'auth/linkInstance',
    async (instanceId: string, thunkAPI) => {
        thunkAPI.dispatch(runOne(LoadingElementsIds.BOARD_USER));
        const {data} = await mleAuthProviderRestClient.post(
            MleAuthProviderEndpoints.LINK_INSTANCE, {
                instanceId
            });

        return data;
    }
);
